import { inject, Injectable } from '@angular/core';
import { Observable, of, tap, BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Customer } from '@shared/models/global.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  apiUrl = environment.api.root;
  private readonly http: HttpClient = inject(HttpClient);
  private readonly CUSTOMER_SESSION_KEY = 'customer_data';

  // Observable pour les données du client
  private customerSubject = new BehaviorSubject<Customer | null>(
    this.getCustomerFromCache()
  );
  public customer$ = this.customerSubject.asObservable();

  constructor() {}

  /**
   * Récupère les données du client
   * Vérifie d'abord si les données sont en cache dans la sessionStorage
   * Si non, effectue une requête HTTP et met en cache le résultat
   * @returns Observable<Customer> Les données du client
   */
  getCustomer(): Observable<Customer> {
    // Vérifier si les données sont en cache
    const cachedCustomer = this.getCustomerFromCache();

    // Si oui, retourner les données du cache et mettre à jour le subject
    if (cachedCustomer) {
      this.customerSubject.next(cachedCustomer);
      return of(cachedCustomer);
    }

    // Sinon, faire une requête HTTP et mettre en cache le résultat
    return this.http.get<Customer>(`${this.apiUrl}/Customer`).pipe(
      tap((customer) => {
        this.saveCustomerToCache(customer);
        this.customerSubject.next(customer);
      })
    );
  }

  /**
   * Récupère les données du client depuis le cache
   * @returns Customer | null Les données du client ou null si elles n'existent pas
   */
  private getCustomerFromCache(): Customer | null {
    try {
      const customerJson = sessionStorage.getItem(this.CUSTOMER_SESSION_KEY);
      return customerJson ? JSON.parse(customerJson) : null;
    } catch (error) {
      console.error('Error parsing customer data from session storage', error);
      return null;
    }
  }

  /**
   * Sauvegarde les données du client dans le cache
   * @param customer Les données du client à mettre en cache
   */
  private saveCustomerToCache(customer: Customer): void {
    try {
      sessionStorage.setItem(
        this.CUSTOMER_SESSION_KEY,
        JSON.stringify(customer)
      );
    } catch (error) {
      console.error('Error saving customer data to session storage', error);
    }
  }

  hasCustomer(): boolean {
    // Vérifie si les données du client existent dans le cache
    return this.getCustomerFromCache() !== null;
  }

  /**
   * Efface les données du client du cache
   * Utile lors d'une déconnexion ou d'un changement d'utilisateur
   */
  clearCustomerCache(): void {
    try {
      sessionStorage.removeItem(this.CUSTOMER_SESSION_KEY);
      this.customerSubject.next(null);
    } catch (error) {
      console.error('Error clearing customer data from session storage', error);
    }
  }
}
